import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Plus,
  Video,
  MapPin,
  User,
  Phone,
  Mail,
  FileText,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

interface Meeting {
  id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  type: string;
  location?: string;
  meeting_url?: string;
  student_id?: string;
  students?: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  status: string;
  notes?: string;
}

const MeetingsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  useEffect(() => {
    fetchMeetings();
  }, [currentDate]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);

      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          students!inner (first_name, last_name, email, phone, branch_id)
        `)
        .eq('students.branch_id', user?.branchId)
        .gte('start_time', startDate.toISOString())
        .lte('start_time', endDate.toISOString())
        .order('start_time', { ascending: true });

      if (error) throw error;
      setMeetings(data || []);
    } catch (error) {
      console.error('Görüşmeler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMeetingTypeLabel = (type: string) => {
    const types: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      consultation: { label: 'Danışmanlık', color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 border border-blue-100 dark:border-blue-800', icon: <User className="w-3 h-3" /> },
      interview: { label: 'Mülakat', color: 'bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 border border-purple-100 dark:border-purple-800', icon: <FileText className="w-3 h-3" /> },
      follow_up: { label: 'Takip', color: 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-200 border border-green-100 dark:border-green-800', icon: <CheckCircle className="w-3 h-3" /> },
      orientation: { label: 'Oryantasyon', color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200 border border-orange-100 dark:border-orange-800', icon: <MapPin className="w-3 h-3" /> },
      other: { label: 'Diğer', color: 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-700', icon: <Video className="w-3 h-3" /> }
    };
    return types[type] || types.other;
  };

  const getStatusIcon = (status: string) => {
    if (status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (status === 'cancelled') {
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
    return <Clock className="w-5 h-5 text-blue-500" />;
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch =
      meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (meeting.students && `${meeting.students.first_name} ${meeting.students.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = filterType === 'all' || meeting.type === filterType;
    const matchesDate = !selectedDate || isSameDay(parseISO(meeting.start_time), selectedDate);

    return matchesSearch && matchesType && matchesDate;
  });

  const upcomingMeetings = filteredMeetings.filter(m => new Date(m.start_time) > new Date());
  const pastMeetings = filteredMeetings.filter(m => new Date(m.start_time) <= new Date());

  const monthDays = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate)
  });

  const getMeetingsForDay = (day: Date) => {
    return meetings.filter(meeting => isSameDay(parseISO(meeting.start_time), day));
  };

  const stats = {
    total: meetings.length,
    upcoming: upcomingMeetings.length,
    today: meetings.filter(m => isSameDay(parseISO(m.start_time), new Date())).length,
    thisWeek: meetings.filter(m => {
      const meetingDate = parseISO(m.start_time);
      const today = new Date();
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      return meetingDate >= today && meetingDate <= weekFromNow;
    }).length
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500 font-medium animate-pulse">Görüşmeler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">Görüşmeler</h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 mt-2 font-medium capitalize">
            {format(currentDate, 'MMMM yyyy', { locale: tr })}
          </p>
        </div>
        <button className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all duration-300 transform hover:-translate-y-0.5">
          <div className="p-1.5 bg-white/20 rounded-lg group-hover:scale-110 transition-transform">
            <Plus className="w-5 h-5" />
          </div>
          <span>Yeni Görüşme</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-6 text-white shadow-xl shadow-blue-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-blue-100 text-sm font-medium">Bu Ay</p>
            <p className="text-3xl font-bold tracking-tight mt-1">{stats.total}</p>
            <p className="text-blue-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Toplam görüşme</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-6 text-white shadow-xl shadow-green-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-green-100 text-sm font-medium">Yaklaşan</p>
            <p className="text-3xl font-bold tracking-tight mt-1">{stats.upcoming}</p>
            <p className="text-green-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Gelecek görüşmeler</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl p-6 text-white shadow-xl shadow-purple-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-purple-100 text-sm font-medium">Bugün</p>
            <p className="text-3xl font-bold tracking-tight mt-1">{stats.today}</p>
            <p className="text-purple-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Görüşme</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-6 text-white shadow-xl shadow-orange-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-orange-100 text-sm font-medium">Bu Hafta</p>
            <p className="text-3xl font-bold tracking-tight mt-1">{stats.thisWeek}</p>
            <p className="text-orange-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Görüşme</p>
          </div>
        </div>
      </div>

      {/* Controls & Search */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-2 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            type="text"
            placeholder="Görüşme veya öğrenci ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400 font-medium"
          />
        </div>
        <div className="h-auto w-px bg-gray-200 dark:bg-gray-700 mx-2 hidden lg:block" />
        <div className="flex gap-2 p-1 overflow-x-auto">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-none rounded-2xl text-gray-700 dark:text-gray-200 font-medium focus:ring-2 focus:ring-blue-500/20 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <option value="all">Tüm Tipler</option>
            <option value="consultation">Danışmanlık</option>
            <option value="interview">Mülakat</option>
            <option value="follow_up">Takip</option>
            <option value="orientation">Oryantasyon</option>
            <option value="other">Diğer</option>
          </select>
        </div>
        <div className="h-auto w-px bg-gray-200 dark:bg-gray-700 mx-2 hidden lg:block" />
        <div className="flex gap-1 p-1 items-center">
          <button
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap min-w-[100px] text-center">
            {format(currentDate, 'MMMM', { locale: tr })}
          </span>
          <button
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            className="p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2.5 ml-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-bold rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
          >
            Bugün
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-8">
        {/* Upcoming Section */}
        <div>
          <div className="flex items-center gap-3 mb-4 ml-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Yaklaşan Görüşmeler ({upcomingMeetings.length})</h3>
          </div>
          <div className="grid gap-4">
            {upcomingMeetings.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-300 dark:border-gray-700">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-500 dark:text-gray-400 font-medium">Yaklaşan görüşme bulunmuyor</p>
              </div>
            ) : (
              upcomingMeetings.map(meeting => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  getMeetingTypeLabel={getMeetingTypeLabel}
                  getStatusIcon={getStatusIcon}
                  onView={() => setSelectedMeeting(meeting)}
                />
              ))
            )}
          </div>
        </div>

        {/* Past Section */}
        {pastMeetings.length > 0 && (
          <div className="pt-8 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 mb-4 ml-2">
              <div className="w-2 h-2 rounded-full bg-gray-400"></div>
              <h3 className="text-xl font-bold text-gray-500 dark:text-gray-400">Geçmiş Görüşmeler ({pastMeetings.length})</h3>
            </div>
            <div className="grid gap-4 opacity-60 hover:opacity-100 transition-opacity">
              {pastMeetings.slice(0, 5).map(meeting => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  getMeetingTypeLabel={getMeetingTypeLabel}
                  getStatusIcon={getStatusIcon}
                  onView={() => setSelectedMeeting(meeting)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedMeeting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedMeeting(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-t-3xl">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedMeeting.title}
                </h2>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 ${getMeetingTypeLabel(selectedMeeting.type).color}`}>
                    {getMeetingTypeLabel(selectedMeeting.type).icon}
                    {getMeetingTypeLabel(selectedMeeting.type).label}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedMeeting(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-8">
              {selectedMeeting.description && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl text-gray-700 dark:text-gray-300 leading-relaxed border border-gray-100 dark:border-gray-800">
                  {selectedMeeting.description}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 flex items-start gap-4 shadow-sm">
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-blue-600">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-bold uppercase mb-0.5">Tarih</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{format(parseISO(selectedMeeting.start_time), 'dd MMMM yyyy', { locale: tr })}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 flex items-start gap-4 shadow-sm">
                    <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-xl text-purple-600">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-bold uppercase mb-0.5">Saat</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {format(parseISO(selectedMeeting.start_time), 'HH:mm')} - {format(parseISO(selectedMeeting.end_time), 'HH:mm')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {selectedMeeting.location ? (
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 flex items-start gap-4 shadow-sm">
                      <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase mb-0.5">Konum</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedMeeting.location}</p>
                      </div>
                    </div>
                  ) : selectedMeeting.meeting_url ? (
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 flex items-start gap-4 shadow-sm">
                      <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-600">
                        <Video className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase mb-0.5">Online</p>
                        <a href={selectedMeeting.meeting_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">
                          Toplantıya Katıl
                        </a>
                      </div>
                    </div>
                  ) : null}

                  {selectedMeeting.students && (
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 flex items-start gap-4 shadow-sm">
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-xl text-green-600">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase mb-0.5">Öğrenci</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {selectedMeeting.students.first_name} {selectedMeeting.students.last_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{selectedMeeting.students.email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedMeeting.notes && (
                <div className="mt-4">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wider flex items-center gap-2">
                    <div className="w-1 h-4 bg-orange-500 rounded-full"></div>
                    Notlar
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 bg-orange-50 dark:bg-orange-900/10 p-4 rounded-xl border border-orange-100 dark:border-orange-900/30">
                    {selectedMeeting.notes}
                  </p>
                </div>
              )}

              <div className="flex gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setSelectedMeeting(null)}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Kapat
                </button>
                <button className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-shadow shadow-lg shadow-blue-500/20 active:scale-95">
                  Düzenle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Extracted Component
const MeetingCard: React.FC<{
  meeting: Meeting;
  getMeetingTypeLabel: (type: string) => any;
  getStatusIcon: (status: string) => any;
  onView: () => void;
}> = ({ meeting, getMeetingTypeLabel, getStatusIcon, onView }) => (
  <div className="group bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 flex items-center gap-5 cursor-pointer" onClick={onView}>

    {/* Date Box */}
    <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
      <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase">
        {format(parseISO(meeting.start_time), 'MMM', { locale: tr })}
      </span>
      <span className="text-2xl font-black text-blue-700 dark:text-blue-300">
        {format(parseISO(meeting.start_time), 'dd')}
      </span>
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 mb-1.5">
        <h4 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors truncate">
          {meeting.title}
        </h4>
        <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${getMeetingTypeLabel(meeting.type).color}`}>
          {getMeetingTypeLabel(meeting.type).label}
        </span>
      </div>

      <div className="flex items-center gap-4 text-sm font-medium text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-gray-400" />
          <span>{format(parseISO(meeting.start_time), 'HH:mm')} - {format(parseISO(meeting.end_time), 'HH:mm')}</span>
        </div>
        {meeting.students && (
          <div className="flex items-center gap-1.5">
            <User className="w-4 h-4 text-gray-400" />
            <span className="truncate">{meeting.students.first_name} {meeting.students.last_name}</span>
          </div>
        )}
      </div>
    </div>

    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors">
        <Eye className="w-5 h-5" />
      </button>
      <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">
        <Edit className="w-5 h-5" />
      </button>
    </div>
  </div>
);

export default MeetingsPage;
